import { fenceAround, findCodeBlocks, findInlineCode } from "#features/conversations/utils/markdownCode.ts";
import type { JSONContent } from "@tiptap/core";

export const MENTION_NODE = "mention";
export const SKILL_NODE = "skill";
export const CODE_MARK = "code";
export const CODE_BLOCK_NODE = "codeBlock";
export const QUOTE_NODE = "blockquote";

// A member's id, as the BFF looks one up: it rejects a whole request over one id that isn't a GUID.
export const MEMBER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// A mention: its name, with "]" and "\" escaped, and its member's id. Only a member id makes one, so text that hides
// anything else in the parentheses the composer never shows stays text, as typed.
const MENTION_MARKUP = new RegExp(String.raw`<@\[((?:[^\]\\]|\\.)+)\]\((${MEMBER_ID.source.slice(1, -1)})\)>`, "gi");

// Only the escapes escapeLabel writes: Slack's formatter writes names unescaped, "\" included, as the old textarea composer did.
const unescapeLabel = (label: string) => label.replace(/\\([\\\]])/g, "$1");
const escapeLabel = (label: string) => label.replace(/[\\\]]/g, "\\$&");

// A skill's slug, as sending reads one: a single word.
export const SKILL_SLUG = /^\S+$/;

// Same rule as parseSelectedSkills, so a token shows exactly where sending reads a skill.
const SKILL_COMMAND = new RegExp(String.raw`(?<=^|\s)\/(${SKILL_SLUG.source.slice(1, -1)})`, "g");
const BACKTICK_RUN = /`+/g;

// Where a token starts in the markup, which also keys it when a sent message lists its parts.
export type MarkupToken =
    | { type: typeof MENTION_NODE; start: number; id: string; label: string }
    | { type: typeof SKILL_NODE; start: number; slug: string };

// Inline code: its text without the backticks, and how many backticks wrap it.
export interface MarkupCode {
    type: typeof CODE_MARK;
    start: number;
    text: string;
    ticks: number;
}

// A fenced code block: its code's lines, and its fence lines as written, the closing one null while the fence is unclosed.
interface MarkupCodeBlock {
    type: typeof CODE_BLOCK_NODE;
    start: number;
    opening: string;
    codeLines: string[];
    closing: string | null;
}

type LinePart = string | MarkupToken | MarkupCode;

// Consecutive quoted lines: each line's prefix as written ("> ", ">", "   > ") and what follows it.
interface MarkupQuote {
    type: typeof QUOTE_NODE;
    start: number;
    lines: Array<{ prefix: string; parts: LinePart[] }>;
}

export type MarkupPart = LinePart | MarkupCodeBlock | MarkupQuote;

// A set of slugs, or a map keyed by them such as the skills' titles.
type KnownSlugs = Pick<ReadonlySet<string>, "has">;

// An end of code that is a backtick, spaces aside, would merge with its fence, so that end gets one space inside the
// fence and loses it when read back. Each end is handled on its own, so any text round-trips, spaces included.
const STARTS_WITH_TICK = /^ *`/;
const ENDS_WITH_TICK = /` *$/;
const padCode = (text: string) => `${STARTS_WITH_TICK.test(text) ? " " : ""}${text}${ENDS_WITH_TICK.test(text) ? " " : ""}`;
// Read code never starts or ends with a backtick itself, so a space before or after one there can only be padding.
const unpadCode = (text: string) => text.slice(/^ +`/.test(text) ? 1 : 0, /` +$/.test(text) ? -1 : undefined);

// A quoted line, one level deep: ">> a" is a quote holding "> a". A ">" followed by text is text, so ">50%" and ">= 3"
// stay as typed; ">" alone is an empty quoted line, as sending trims a last "> " to it.
const QUOTE_PREFIX = /^ {0,3}>(?=[ >]|$) ?/;

export const isQuotePrefix = (prefix: string) => prefix.match(QUOTE_PREFIX)?.[0] === prefix;

const lineToParts = (line: string, offset: number, knownSlugs: KnownSlugs): LinePart[] => {
    const codeSpans = findInlineCode(line);
    const isInCode = (start: number, end: number) => codeSpans.some(span => start < span.end && end > span.start);
    const pieces: Array<(MarkupToken | MarkupCode) & { end: number }> = codeSpans.map(({ start, end, ticks }) => ({
        type: CODE_MARK,
        start,
        end,
        ticks,
        text: unpadCode(line.slice(start + ticks, end - ticks))
    }));

    for (const match of line.matchAll(MENTION_MARKUP)) {
        const end = match.index + match[0].length;
        if (!isInCode(match.index, end)) {
            pieces.push({ type: MENTION_NODE, start: match.index, end, id: match[2], label: unescapeLabel(match[1]) });
        }
    }

    // A mention whose name holds " /slug" stays a mention.
    const mentions = pieces.filter(piece => piece.type === MENTION_NODE);
    for (const match of line.matchAll(SKILL_COMMAND)) {
        const start = match.index;
        const end = start + match[0].length;
        if (knownSlugs.has(match[1]) && !isInCode(start, end) && !mentions.some(mention => start < mention.end && end > mention.start)) {
            pieces.push({ type: SKILL_NODE, start, end, slug: match[1] });
        }
    }

    pieces.sort((a, b) => a.start - b.start);

    const parts: LinePart[] = [];
    let cursor = 0;
    for (const { end, ...piece } of pieces) {
        if (piece.start > cursor) {
            parts.push(line.slice(cursor, piece.start));
        }
        parts.push({ ...piece, start: offset + piece.start });
        cursor = end;
    }
    if (cursor < line.length) {
        parts.push(line.slice(cursor));
    }

    return parts;
};

// One line's text, mentions, known skills and inline code, as a sent message reads a line.
export const readLine = (line: string, knownSlugs: KnownSlugs) => lineToParts(line, 0, knownSlugs);

const markupLines = (markup: string) => {
    let offset = 0;

    return markup.split("\n").map(line => {
        const lineStart = offset;
        offset += line.length + 1;

        return { line, lineStart };
    });
};

// Each fenced code block of the lines, keyed by its opening fence's line, with the index of its last line.
const codeBlocksByFirstLine = (lines: Array<{ line: string; lineStart: number }>) =>
    new Map(findCodeBlocks(lines.map(({ line }) => line)).map(({ first, last, isClosed }): [number, MarkupCodeBlock & { last: number }] => {
        const blockLines = lines.slice(first, last + 1).map(({ line }) => line);

        return [first, {
            type: CODE_BLOCK_NODE,
            start: lines[first].lineStart,
            opening: blockLines[0],
            codeLines: blockLines.slice(1, isClosed ? -1 : undefined),
            closing: isClosed ? blockLines[blockLines.length - 1] : null,
            last
        }];
    }));

// The line's parts are read with its prefix, so a "/slug" right after ">" stays text, as sending reads it.
const withoutPrefix = ([first, ...rest]: LinePart[], prefix: string): LinePart[] => {
    const text = (first as string).slice(prefix.length);

    return text === "" ? rest : [text, ...rest];
};

// The quoted lines from `first` on, up to a line that isn't quoted or opens a code block.
const readQuote = (lines: Array<{ line: string; lineStart: number }>, first: number, blockAt: ReadonlyMap<number, unknown>, knownSlugs: KnownSlugs) => {
    const quoted: MarkupQuote["lines"] = [];
    let index = first;
    for (; index < lines.length && !blockAt.has(index); index++) {
        const { line, lineStart } = lines[index];
        const prefix = line.match(QUOTE_PREFIX)?.[0];
        if (prefix === undefined) {
            break;
        }
        quoted.push({ prefix, parts: withoutPrefix(lineToParts(line, lineStart, knownSlugs), prefix) });
    }

    return quoted.length > 0 ? { type: QUOTE_NODE, start: lines[first].lineStart, lines: quoted, last: index - 1 } satisfies MarkupQuote & { last: number } : undefined;
};

// The text, mentions and known skills of a message, in order, read line by line as the composer reads them. A code
// block or a quote drops the line break after it, not the one before: a block only shows that one after a blank line.
export const parseMarkup = (markup: string, knownSlugs: KnownSlugs): MarkupPart[] => {
    const lines = markupLines(markup);
    const blockAt = codeBlocksByFirstLine(lines);
    const parts: MarkupPart[] = [];
    let followsBlock = false;
    let index = 0;
    while (index < lines.length) {
        if (index > 0 && !followsBlock) {
            parts.push("\n");
        }
        const block = blockAt.get(index) ?? readQuote(lines, index, blockAt, knownSlugs);
        followsBlock = block !== undefined;
        if (block) {
            const { last, ...part } = block;
            parts.push(part);
            index = last + 1;
        } else {
            parts.push(...lineToParts(lines[index].line, lines[index].lineStart, knownSlugs));
            index++;
        }
    }

    return parts;
};

const NO_SLUGS: KnownSlugs = new Set<string>();

// The ids of the members a message mentions, in order, repeats included. Mention markup in code is text, as it shows.
export const mentionedMemberIds = (markup: string) =>
    parseMarkup(markup, NO_SLUGS)
        .flatMap(part => (typeof part !== "string" && part.type === QUOTE_NODE ? part.lines.flatMap(line => line.parts) : [part]))
        .flatMap(part => (typeof part !== "string" && part.type === MENTION_NODE ? [part.id] : []));

const partToContent = (part: LinePart): JSONContent => {
    if (typeof part === "string") {
        return { type: "text", text: part };
    }
    switch (part.type) {
        case MENTION_NODE:
            return { type: MENTION_NODE, attrs: { id: part.id, label: part.label } };
        case SKILL_NODE:
            return { type: SKILL_NODE, attrs: { slug: part.slug } };
        default:
            return { type: "text", text: part.text, marks: [{ type: CODE_MARK, attrs: { ticks: part.ticks } }] };
    }
};

// The block's code as one text, and whether it had a line at all, since "```\n```" and "```\n\n```" both hold "".
const codeBlockToContent = ({ opening, codeLines, closing }: MarkupCodeBlock): JSONContent => {
    const code = codeLines.join("\n");

    return {
        type: CODE_BLOCK_NODE,
        attrs: { opening, closing, hasCodeLine: codeLines.length > 0 },
        ...(code !== "" && { content: [{ type: "text", text: code }] })
    };
};

// One paragraph per line rather than hard breaks: ProseMirror can't place the caret after a trailing hard break. A
// fenced block is one code block node, and a quoted line a paragraph holding its prefix as written.
export const markupToDoc = (markup: string, knownSlugs: ReadonlySet<string>): JSONContent => {
    const lines = markupLines(markup);
    const blockAt = codeBlocksByFirstLine(lines);
    const content: JSONContent[] = [];
    let index = 0;
    while (index < lines.length) {
        const block = blockAt.get(index);
        const quote = block ? undefined : readQuote(lines, index, blockAt, knownSlugs);
        if (block) {
            content.push(codeBlockToContent(block));
            index = block.last + 1;
        } else if (quote) {
            content.push(...quote.lines.map(({ prefix, parts }): JSONContent => ({
                type: "paragraph",
                attrs: { quote: prefix },
                ...(parts.length > 0 && { content: parts.map(partToContent) })
            })));
            index = quote.last + 1;
        } else {
            const parts = lineToParts(lines[index].line, lines[index].lineStart, knownSlugs).map(partToContent);
            content.push(parts.length > 0 ? { type: "paragraph", content: parts } : { type: "paragraph" });
            index++;
        }
    }

    return { type: "doc", content };
};

// What follows an opening fence's backticks (a language, or any text), which is sent with the block.
export const codeInfo = (opening: string) => opening.replace(/^ *`+/, "").trim();

// The span's own backtick count when its text holds no run that long, so a parsed span comes back as it was; else the
// fewest backticks no run in the text matches, so a backtick typed inside code doesn't close it.
export const codeTicks = (text: string, preferred: number) => {
    const runs = new Set((text.match(BACKTICK_RUN) ?? []).map(run => run.length));
    if (!runs.has(preferred)) {
        return preferred;
    }
    let ticks = 1;
    while (runs.has(ticks)) {
        ticks++;
    }

    return ticks;
};

const textToMarkup = ({ text = "", marks }: JSONContent) => {
    const code = marks?.find(mark => mark.type === CODE_MARK);
    if (!code) {
        return text;
    }
    const fence = "`".repeat(codeTicks(text, Number(code.attrs?.ticks ?? 1)));

    return `${fence}${padCode(text)}${fence}`;
};

const inlineToMarkup = (node: JSONContent): string => {
    switch (node.type) {
        case "text":
            return textToMarkup(node);
        case "hardBreak":
            return "\n";
        case MENTION_NODE:
            return `<@[${escapeLabel(String(node.attrs?.label ?? ""))}](${node.attrs?.id})>`;
        case SKILL_NODE:
            return `/${node.attrs?.slug}`;
        default:
            return (node.content ?? []).map(inlineToMarkup).join("");
    }
};

// An unclosed fence runs to the end, so one with anything after it (a paste, say) gets its opening's fence to close it.
const codeBlockToMarkup = ({ attrs, content }: JSONContent, isLast: boolean) => {
    const code = (content ?? []).map(node => node.text ?? "").join("");
    const { opening = "```", closing = "```", hasCodeLine = true } = attrs ?? {};
    // A code line that reads as a fence (a pasted README, say) would close the block early, so both fences outgrow it.
    const fence = fenceAround(code.split("\n"), (opening as string).match(/`+/)?.[0] ?? "```");
    const widened = (line: string) => line.replace(/`+/, run => (run.length >= fence.length ? run : fence));
    const closingLine = closing === null ? (isLast ? null : fence) : widened(closing as string);

    return [widened(opening as string), ...(code !== "" || hasCodeLine ? [code] : []), ...(closingLine ? [closingLine] : [])].join("\n");
};

// A quoted paragraph's prefix as written, also before each of its hard breaks, which the editor shows inside the quote.
const paragraphToMarkup = ({ attrs, content }: JSONContent) => {
    const prefix = (attrs?.quote as string | null | undefined) ?? "";

    return prefix + (content ?? []).map(inlineToMarkup).join("").replaceAll("\n", `\n${prefix}`);
};

export const docToMarkup = (doc: JSONContent): string =>
    (doc.content ?? [])
        .map((block, index, blocks) => (block.type === CODE_BLOCK_NODE ? codeBlockToMarkup(block, index === blocks.length - 1) : paragraphToMarkup(block)))
        .join("\n");
